import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { ShieldCheck, Settings as SettingsIcon } from 'lucide-react';
import {
  AppLayout,
  AppSwitcher,
  toWorkspaceApps,
  PlatformTopbar,
  SharedSidebarShell,
  TenantSwitcher,
  UserMenu,
  UserMenuItem,
  accountSubtitle,
} from '@zafroi/ui-kit';
import type { UserMenuIdentity } from '@zafroi/ui-kit';
import { sidebarNav } from '@/config/nav';
import { Logo } from '@/components/Logo';
import { useTenant } from '@/services/tenant-context';
import { useAuth } from '@/services/auth-context';
import { useKoshLabels } from '@/hooks/useKosh';
import type { KoshLabelKey } from '@/types/kosh';

// Nav items whose label is driven by the tenant's business template
// (Commerce → "Orders", Real Estate → "Bookings", …). Others are fixed.
const NAV_LABEL_KEYS: Record<string, KoshLabelKey> = {
  customers: 'customer',
  contracts: 'contract',
  invoices: 'invoice',
  quotes: 'proposal',
};

// Nav items requiring a permission to be shown. Catalog (incl. the operator-only
// Plans gate) moved to zafroi-catalog-app (ADR-035/036); none remain in Kosh, but
// the mechanism stays for future per-permission Kosh items.
const NAV_PERMISSION: Record<string, string> = {};

const COLLAPSE_KEY = 'zafroi.kosh.sidebar.collapsed.v1';
const BASE_DOC_TITLE = 'ZAFROI Kosh';
// Plane boundary (ADR-024): a PLATFORM_ADMIN's home is the control plane; the
// tenant workspace redirects them there. Safe vs stale cache — the auth-context
// only sets status='authenticated' after a fresh /auth/me, so user.tier is fresh.
const PLATFORM_APP_URL =
  import.meta.env.VITE_PLATFORM_APP_URL || 'https://platform.zafroi.com/dashboard';

export function AppShell() {
  const navigate = useNavigate();
  const { tenants, currentTenant, setCurrentTenant } = useTenant();
  const { user, logout, status } = useAuth();
  const { labelPlural } = useKoshLabels();
  const isPlatformAdmin = status === 'authenticated' && user?.tier === 'PLATFORM_ADMIN';
  useEffect(() => {
    if (isPlatformAdmin) window.location.replace(PLATFORM_APP_URL);
  }, [isPlatformAdmin]);

  // Permission-gated nav items — hidden unless the user holds the permission. The
  // Plans catalog is operator-only (ADR-035), so only the operator sees it.
  const permissions = user?.permissions ?? [];
  const nav = useMemo(
    () =>
      sidebarNav
        .map((group) => ({
          ...group,
          items: group.items
            .filter((item) => {
              const need = NAV_PERMISSION[item.id];
              return !need || permissions.includes(need);
            })
            .map((item) => {
              const key = NAV_LABEL_KEYS[item.id];
              return key ? { ...item, label: labelPlural(key) } : item;
            }),
        }))
        .filter((group) => group.items.length > 0),
    [labelPlural, permissions],
  );
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
  });

  // Keep the document title stable for now — feature pages will layer in
  // their own "work-to-do" signals later.
  useEffect(() => {
    document.title = BASE_DOC_TITLE;
    return () => {
      document.title = BASE_DOC_TITLE;
    };
  }, []);

  // Live user identity from the JWT session. Falls back to "—" if for some
  // reason the user is missing (AuthGuard should have already redirected,
  // so this is defensive only).
  const userMenuIdentity: UserMenuIdentity = useMemo(
    () =>
      user
        ? {
            name: user.fullName,
            email: user.email,
            subtitle: accountSubtitle({ tier: user.tier, role: user.role, roles: user.roles }),
          }
        : { name: '—', subtitle: 'Not signed in' },
    [user],
  );

  const handleToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // non-fatal
      }
      return next;
    });
  };

  // Platform admins don't belong in the tenant workspace — show a brief
  // redirecting state while the effect above sends them to the control plane.
  if (isPlatformAdmin) {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-sm text-muted-foreground">
        Redirecting to the control plane…
      </div>
    );
  }

  const handleSignOut = async () => {
    await logout();
    // The AuthGuard will see the unauthenticated state on the next render
    // and hard-redirect to the canonical zafroi.com/login.
    navigate('/dashboard', { replace: true });
  };

  return (
    <AppLayout
      sidebar={
        <SharedSidebarShell
          nav={nav}
          collapsed={collapsed}
          onToggle={handleToggle}
          header={
            <Logo
              variant={collapsed ? 'mark' : 'lockup'}
              tagline="Kosh"
              glow
            />
          }
          footer={
            !collapsed ? (
              <div className="flex items-center justify-between">
                <span>v0.1.0</span>
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] uppercase tracking-wider">
                  MVP1
                </span>
              </div>
            ) : null
          }
        />
      }
      topbar={
        <PlatformTopbar
          leading={
            <>
              <AppSwitcher
                apps={toWorkspaceApps(user?.workspace?.apps)}
                currentAppId="kosh"
                onAppSelect={(app) => {
                  // Same module → client-side nav to its dashboard. Other modules
                  // are SEPARATE SPAs at app.zafroi.com/<module>/ — let the <a href>
                  // do a full-page load (do NOT client-route them, or this SPA 404s).
                  if (app.id === 'kosh') {
                    navigate('/dashboard');
                    return false;
                  }
                  return undefined;
                }}
              />
              <span className="hidden text-sm text-muted-foreground md:inline">
                /
              </span>
              <TenantSwitcher
                tenants={tenants}
                currentTenantId={currentTenant.id}
                onTenantChange={setCurrentTenant}
              />
            </>
          }
          center={null}
          trailing={
            <UserMenu user={userMenuIdentity} onSignOut={handleSignOut}>
              <UserMenuItem onSelect={() => navigate('/setup/organization')}>
                <SettingsIcon className="h-4 w-4" />
                Account settings
              </UserMenuItem>
              <UserMenuItem>
                <ShieldCheck className="h-4 w-4" />
                Security
              </UserMenuItem>
            </UserMenu>
          }
        />
      }
    >
      <Outlet />
    </AppLayout>
  );
}
